/**
 * @voltxampere/engine — Transient integration method types
 *
 * SPECS §3.8 ships three implicit integration methods for the
 * transient solver — Backward Euler (BE, 1st order), Trapezoidal
 * (TRAP, 2nd order), and Gear-2 / BDF2 (2nd order). Each method
 * generates a *companion model* for energy-storage components
 * (capacitors and inductors) by replacing the time derivative with
 * a finite-difference approximation that depends only on the
 * previous-step state.
 *
 * For a capacitor C with `i = C dV/dt`:
 *
 *   BE:     i(t) = (C/h)·(V(t) − V(t−h))
 *   TRAP:   i(t) = (2C/h)·(V(t) − V(t−h)) − i(t−h)
 *   Gear-2: i(t) = (3C/(2h))·V(t) − (2C/h)·V(t−h) + (C/(2h))·V(t−2h)
 *
 * In Norton form `i(t) = Geq · V(t) + Ieq` these become:
 *
 *   BE:     Geq = C/h,        Ieq = −(C/h)·V(t−h)
 *   TRAP:   Geq = 2C/h,       Ieq = −(2C/h)·V(t−h) − i(t−h)
 *   Gear-2: Geq = 3C/(2h),    Ieq = −(2C/h)·V(t−h) + (C/(2h))·V(t−2h)
 *
 * Inductor companions are the dual (V↔I, C↔L). Per Sprint 6's
 * planner contract the inductor keeps the SPECS §3.1 augmented row
 * (`["vsource"]` augmentation) even in transient — the row reads
 *
 *   V(t) − D · I(t) = Ieq
 *
 * where `D = Geq` is the equivalent inductive resistance. The stamp
 * places `−Geq` on the augmented diagonal and `Ieq` on the row's RHS.
 *
 * This file declares the shared types only. Concrete implementations
 * live in `backward-euler.ts`, `trapezoidal.ts`, `gear-2.ts`, with
 * a thin `dispatcher.ts` that picks the right one given the method
 * + first-step state.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
/** Map a schema-level method label to the engine-internal name. */
export function resolveIntegrationMethod(label) {
    switch (label) {
        case "be":
            return "BE";
        case "trap":
            return "TRAP";
        case "gear2":
            return "Gear-2";
    }
}
//# sourceMappingURL=types.js.map